import { useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Helmet } from "react-helmet-async";
import {
  Shield, CheckCircle, BadgeCheck, ArrowRight, Lock,
  TrendingUp, Eye, ShoppingCart, Briefcase, BookOpen,
  Heart, Globe, ChevronDown, ChevronUp, Zap, Users, Award
} from "lucide-react";

const WEBSITE_CATEGORIES = [
  "E-commerce / Online Store",
  "Digital Products / Downloads",
  "SaaS / Software",
  "Freelance / Services",
  "Blog / Content Site",
  "Educational Platform",
  "Health & Wellness",
  "Finance / Investment",
  "Travel & Hospitality",
  "Other",
];

const WEBSITE_AGE_OPTIONS = [
  "Less than 1 month",
  "1–6 months",
  "6–12 months",
  "1–2 years",
  "More than 2 years",
];

const FAQS = [
  {
    q: "Is the first month really free?",
    a: "Yes, completely free. We only charge $10/month starting from month 2. If you cancel before month 2, you pay nothing at all.",
  },
  {
    q: "What if my site doesn't pass the review?",
    a: "We'll email you explaining why and what you can improve. No charge whatsoever.",
  },
  {
    q: "How long does review take?",
    a: "1–2 business days. You'll get an email either way — approved or not.",
  },
  {
    q: "What does the badge look like?",
    a: "After approval we send you a small HTML snippet. It displays a verified badge on your site that visitors can click to see your verification details.",
  },
  {
    q: "What happens if I cancel?",
    a: "Your badge deactivates and your domain returns to normal checker results at the end of the billing period.",
  },
  {
    q: "Can I have multiple domains?",
    a: "Yes, each domain needs a separate subscription at $10/month each.",
  },
  {
    q: "Will the badge work on Shopify, WordPress, Wix?",
    a: "Yes, the embed code is plain HTML and works on any platform.",
  },
  {
    q: "What if my site gets flagged after verification?",
    a: "We monitor all verified sites. If a site is later found to be unsafe, the badge is immediately revoked and the domain returns to normal checker results.",
  },
];

const WHO_ITS_FOR = [
  { icon: Globe, title: "New & Growing Websites", desc: "Every new site looks the same to a cautious visitor — whether you launched yesterday or last year. The badge makes you look established immediately." },
  { icon: ShoppingCart, title: "Online Stores", desc: "Build trust before checkout, whether you sell physical products, digital downloads, or services." },
  { icon: Zap, title: "SaaS & Startups", desc: "Show users you're a real, accountable company before they connect a payment method or hand over their data." },
  { icon: Briefcase, title: "Freelancers & Agencies", desc: "Show potential clients you're a real, vetted operation before they pick up the phone." },
  { icon: BookOpen, title: "Blogs & Educational Sites", desc: "Readers, parents, and students need to know you're legitimate before they engage or enroll." },
  { icon: Heart, title: "Health & Wellness Sites", desc: "Trust is everything in sensitive niches. Give visitors certainty before they share personal information." },
];

const WORKER_URL = "https://api.transparencybadge.com";

function BadgeSVG({ size = 72 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="sg_gv" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="100%" stopColor="#0ea5e9" />
        </linearGradient>
        <linearGradient id="sb_gv" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1e4976" />
          <stop offset="100%" stopColor="#0f2d4a" />
        </linearGradient>
        <filter id="gl_gv">
          <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <path d="M20 3L5 9v10c0 8.5 6.5 16.4 15 18.4C29.5 35.4 36 27.5 36 19V9L20 3z" fill="url(#sb_gv)" stroke="url(#sg_gv)" strokeWidth="1.5" />
      <path d="M20 6.5L8 11.5v8.5c0 6.8 5.2 13.1 12 14.9C26.8 33.1 32 26.8 32 20v-8.5L20 6.5z" fill="none" stroke="rgba(99,179,237,0.15)" strokeWidth="0.5" />
      <circle cx="28" cy="28" r="9" fill="#0f2744" />
      <circle cx="28" cy="28" r="8" fill="url(#sg_gv)" filter="url(#gl_gv)" />
      <path d="M24 28l2.5 2.5L32 24" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function GetVerified() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    country: "",
    domain: "",
    category: "",
    description: "",
    websiteAge: "",
    hasPrivacy: "",
    hasTerms: "",
    hasRefund: "",
    isLive: "",
    additionalInfo: "",
  });

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.fullName.trim()) e.fullName = "Full name is required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email is required";
    if (!form.country.trim()) e.country = "Country is required";
    if (!form.domain.trim()) e.domain = "Domain is required";
    if (!form.category) e.category = "Please select a category";
    if (!form.description.trim() || form.description.length < 30) e.description = "Please describe your website (min 30 characters)";
    if (!form.websiteAge) e.websiteAge = "Please select how long your site has been active";
    if (!form.hasPrivacy) e.hasPrivacy = "Please answer this";
    if (!form.hasTerms) e.hasTerms = "Please answer this";
    if (!form.hasRefund) e.hasRefund = "Please answer this";
    if (!form.isLive) e.isLive = "Please answer this";
    return e;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    setSubmitError("");
    try {
      const response = await fetch(`${WORKER_URL}/api/badge/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: form.fullName, email: form.email, domain: form.domain,
          category: form.category, description: form.description, country: form.country,
          websiteAge: form.websiteAge, hasPrivacy: form.hasPrivacy, hasTerms: form.hasTerms,
          hasRefund: form.hasRefund, isLive: form.isLive, additionalInfo: form.additionalInfo,
        }),
      });
      const data = await response.json();
      if (!response.ok) { setSubmitError(data.error || "Something went wrong. Please try again."); return; }
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setSubmitError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <Helmet><title>Application Submitted — Transparency Badge</title></Helmet>
        <section className="py-24 container max-w-lg mx-auto text-center space-y-6 animate-fade-in">
          <div className="w-20 h-20 rounded-full bg-safe-bg flex items-center justify-center mx-auto">
            <BadgeCheck className="h-10 w-10 text-safe" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">Application Received!</h1>
          <p className="text-muted-foreground text-lg">
            Thank you! We've received your application for{" "}
            <span className="font-medium text-foreground">{form.domain}</span>.
            Our team will review your website and email you at{" "}
            <span className="font-medium text-foreground">{form.email}</span> within 1–2 business days.
          </p>
          <div className="bg-accent/50 rounded-xl p-5 text-left space-y-3">
            <p className="text-sm font-medium text-foreground">What happens next:</p>
            {[
              "We run automatic threat intelligence checks on your domain",
              "Your application passes our eligibility check",
              "You receive an email with approval or feedback",
              "If approved — you get your badge embed code and payment details",
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <p className="text-sm text-muted-foreground">{step}</p>
              </div>
            ))}
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Get Verified Badge for Your Website — Transparency Badge</title>
        <meta name="description" content="Get a Verified Badge with continuous automated monitoring and a one-time eligibility check. First month free, then $10/month — cancel anytime." />
        <meta property="og:title" content="Get Verified Badge for Your Website" />
        <meta property="og:description" content="Apply for continuous domain monitoring and a verified trust badge. One-time eligibility check, then ongoing automated protection. First month free." />
        <meta property="og:url" content="https://transparencybadge.com/get-verified" />
        <link rel="canonical" href="https://transparencybadge.com/get-verified" />
      </Helmet>

      {/* ── HERO: BADGE FRONT AND CENTER ───────────────────────────────────── */}
      <section className="relative overflow-hidden border-b" style={{ background: "linear-gradient(160deg, #0f1f3d 0%, #0d2240 40%, #0f2744 70%, #0a1628 100%)" }}>
        {/* subtle grid overlay */}
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "linear-gradient(rgba(99,179,237,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(99,179,237,0.4) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />

        <div className="relative container py-16 md:py-24">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">

              {/* Left: copy */}
              <div className="space-y-6 animate-fade-in">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: "rgba(99,179,237,0.15)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.2)" }}>
                  <BadgeCheck className="h-3.5 w-3.5" />
                  Verified Badge Program
                </span>

                <h1 className="text-4xl md:text-5xl font-bold leading-tight text-balance" style={{ color: "#e2e8f0" }}>
                  Turn "Unknown Website" Into{" "}
                  <span style={{ color: "#38bdf8" }}>Instant Trust</span>
                </h1>

                <p className="text-lg" style={{ color: "#90cdf4" }}>
                  Visitors decide whether to trust your website in under 3 seconds.
                  The Transparency Badge gives them a reason to stay — and take you seriously.
                </p>

                <div className="space-y-3">
                  {[
                    "Your site goes from 'New / Unknown' → 'Verified' instantly",
                    "Standardized eligibility check, then continuous automated monitoring",
                    "First month completely free. No credit card to apply.",
                  ].map((t) => (
                    <div key={t} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: "rgba(34,197,94,0.2)" }}>
                        <CheckCircle className="h-3 w-3" style={{ color: "#22c55e" }} />
                      </div>
                      <span className="text-sm" style={{ color: "#cbd5e1" }}>{t}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    size="lg"
                    className="h-auto py-3.5 px-6 text-base font-semibold"
                    style={{ background: "linear-gradient(135deg, #38bdf8, #0ea5e9)", color: "#fff", border: "none" }}
                    onClick={() => document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" })}
                  >
                    Get Verified Free
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <div className="flex items-center gap-2 text-sm" style={{ color: "#90cdf4" }}>
                    <Lock className="h-4 w-4" />
                    No credit card required
                  </div>
                </div>


              </div>

              {/* Right: Badge + live preview */}
              <div className="flex flex-col items-center gap-8">
                {/* The actual badge — hero size */}
                <div className="relative">
                  {/* glow effect */}
                  <div className="absolute inset-0 rounded-3xl blur-2xl opacity-40" style={{ background: "radial-gradient(circle, #38bdf8, #0ea5e9)", transform: "scale(0.8)" }} />
                  <div className="relative" style={{
                    background: "linear-gradient(135deg, #0f2744 0%, #1a3a5c 50%, #0d3554 100%)",
                    borderRadius: "24px",
                    padding: "32px 48px",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,179,237,0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
                    display: "flex",
                    flexDirection: "column" as const,
                    alignItems: "center",
                    gap: "16px",
                    minWidth: "260px",
                  }}>
                    <BadgeSVG size={80} />
                    <div style={{ textAlign: "center" }}>
                      <p style={{ fontSize: "17px", fontWeight: "700", color: "#e2e8f0", letterSpacing: "0.02em", marginBottom: "6px" }}>yoursite.com</p>
                      <div style={{ width: "80%", height: "1px", background: "rgba(99,179,237,0.3)", margin: "0 auto 8px" }} />
                      <p style={{ fontSize: "12px", color: "#63b3ed", fontWeight: "700", letterSpacing: "0.08em", textTransform: "uppercase" as const, marginBottom: "3px" }}>✓ Verified by</p>
                      <p style={{ fontSize: "11px", color: "#90cdf4", letterSpacing: "0.03em" }}>Transparency Badge</p>
                    </div>
                    {/* pulsing dot */}
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "#22c55e" }} />
                        <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#22c55e" }} />
                      </span>
                      <span style={{ fontSize: "11px", color: "#68d391", fontWeight: "600" }}>Active verification</span>
                    </div>
                  </div>
                </div>

                {/* before / after */}
                <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
                  <div className="rounded-xl p-3 text-center" style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.25)" }}>
                    <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "#f59e0b" }}>Before</p>
                    <p className="text-sm font-semibold" style={{ color: "#fde68a" }}>New / Unknown</p>
                    <p className="text-xs mt-1" style={{ color: "#92400e" }}>Visitors leave</p>
                  </div>
                  <div className="rounded-xl p-3 text-center" style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)" }}>
                    <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "#22c55e" }}>After</p>
                    <p className="text-sm font-semibold" style={{ color: "#86efac" }}>Verified ✓</p>
                    <p className="text-xs mt-1" style={{ color: "#166534" }}>Visitors trust & engage</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── PROBLEM SECTION ────────────────────────────────────────────────── */}
      <section className="py-16 border-b bg-background">
        <div className="container max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">The Problem Every Legitimate Website Faces</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              You built something real. But to a first-time visitor, your website looks exactly the same as every scam site they've been warned about. That's not fair — and it's costing you sales every single day.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            {[
              { stat: "70%", desc: "of visitors check whether a website is safe before buying, signing up, or sharing information" },
              { stat: "3 sec", desc: "is all visitors take to decide whether to trust an unfamiliar website" },
              { stat: "3x", desc: "more conversions on websites that display visible, verifiable trust signals" },
            ].map((item) => (
              <div key={item.stat} className="bg-card border rounded-xl p-6 text-center space-y-2">
                <p className="text-4xl font-bold text-primary">{item.stat}</p>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* What visitors see without badge */}
          <div className="bg-card border rounded-2xl overflow-hidden">
            <div className="bg-muted/50 px-5 py-3 border-b">
              <p className="text-sm font-semibold text-foreground">What a cautious visitor sees when they check your website</p>
            </div>
            <div className="p-5 grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)" }}>
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4" style={{ color: "#f59e0b" }} />
                  <p className="text-sm font-bold" style={{ color: "#f59e0b" }}>Without the Badge</p>
                </div>
                {["Domain registered recently", "No verification on record", "No eligibility check on record", "Result: NEW / UNKNOWN"].map((t, i) => (
                  <div key={t} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: i === 3 ? "#ef4444" : "#94a3b8" }} />
                    <span className={i === 3 ? "font-semibold text-destructive" : ""}>{t}</span>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground pt-1">70% of these visitors leave without taking action.</p>
              </div>
              <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)" }}>
                <div className="flex items-center gap-2">
                  <BadgeCheck className="h-4 w-4" style={{ color: "#22c55e" }} />
                  <p className="text-sm font-bold" style={{ color: "#22c55e" }}>With the Badge</p>
                </div>
                {["Passed threat intelligence scan", "Passed eligibility check", "Monitored and verified monthly", "Result: VERIFIED ✓"].map((t, i) => (
                  <div key={t} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-3 w-3 shrink-0" style={{ color: "#22c55e" }} />
                    <span className={i === 3 ? "font-semibold" : "text-muted-foreground"} style={i === 3 ? { color: "#22c55e" } : {}}>{t}</span>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground pt-1">Visitors see proof your site is real. They stay. They engage.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ───────────────────────────────────────────────────── */}
      <section className="py-16 border-b" style={{ background: "linear-gradient(180deg, rgba(99,102,241,0.03) 0%, transparent 100%)" }}>
        <div className="container max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">How It Works</h2>
            <p className="text-muted-foreground">From application to verified — in 1–2 business days</p>
          </div>
          <div className="relative">
            {/* connecting line */}
            <div className="absolute left-[19px] top-8 bottom-8 w-px bg-gradient-to-b from-primary/40 to-transparent hidden md:block" />
            <div className="space-y-4">
              {[
                { step: 1, icon: "📝", title: "Fill the 2-minute form below", desc: "Tell us about your website — your domain, category, and a brief description of what you do." },
                { step: 2, icon: "🔍", title: "We run automatic safety checks", desc: "Threat intelligence databases, SSL validation, domain reputation scans — all within seconds of your submission." },
                { step: 3, icon: "👤", title: "One-time eligibility check", desc: "Someone on our team confirms your site meets our published criteria — working contact info, readable policies, honest content — before your automated monitoring subscription begins." },
                { step: 4, icon: "✅", title: "Badge activates — you get the code", desc: "You receive your embed code by email. One line of HTML, paste it anywhere on your site. Works on Shopify, WordPress, Wix, or custom sites." },
                { step: 5, icon: "📈", title: "Your checker result changes permanently", desc: "Anyone who checks your domain now sees 'Verified by Transparency Badge' instead of 'New / Unknown'. That result follows your domain everywhere." },
              ].map((item) => (
                <div key={item.step} className="flex items-start gap-4 bg-card border rounded-xl p-5 hover:shadow-sm transition-shadow">
                  <div className="w-10 h-10 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{item.icon} {item.title}</p>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ────────────────────────────────────────────────── */}
      <section className="py-16 border-b bg-background">
        <div className="container max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Everything Included</h2>
            <p className="text-muted-foreground">One badge. Everything you need to turn skeptical visitors into confident buyers.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { icon: Shield, title: "Automatic threat intelligence scan", desc: "Your domain is checked against public phishing, malware, and abuse databases before your badge is issued." },
              { icon: Users, title: "One-time eligibility check", desc: "Before your subscription activates, someone on our team confirms your site meets our published criteria — a standardized check, not a subjective one." },
              { icon: TrendingUp, title: "Checker result changes to Verified", desc: "Anyone running a safety check on your domain sees 'Verified' instead of 'Unknown'. That's the core value." },
              { icon: Award, title: "Embeddable badge for your website", desc: "A clean, professional widget you can place anywhere on your site. Auto-shows your domain name and links to your verification page." },
              { icon: Eye, title: "Public verification page", desc: "Visitors can click the badge and see your full verification details — what was reviewed, when, and by whom." },
              { icon: Zap, title: "Continuous monthly monitoring", desc: "We don't just verify once and forget. Your domain is monitored every month your subscription is active." },
            ].map((f) => (
              <div key={f.title} className="flex items-start gap-4 bg-card border rounded-xl p-5">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <f.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-foreground text-sm">{f.title}</p>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR ───────────────────────────────────────────────────── */}
      <section className="py-16 border-b" style={{ background: "linear-gradient(180deg, rgba(99,102,241,0.03) 0%, transparent 100%)" }}>
        <div className="container">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Built for Any Website That Needs to Earn Trust Faster</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">If your visitors have ever hesitated before clicking "buy" or "contact us", the Transparency Badge is for you.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {WHO_ITS_FOR.map((item) => (
              <div key={item.title} className="bg-card border rounded-xl p-5 space-y-2 hover:border-primary/40 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <p className="font-semibold text-foreground">{item.title}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* ── PRICING ────────────────────────────────────────────────────────── */}
      <section className="py-16 border-b" style={{ background: "linear-gradient(180deg, rgba(99,102,241,0.04) 0%, transparent 100%)" }}>
        <div className="container max-w-md mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Simple, Honest Pricing</h2>
          <p className="text-muted-foreground mb-8">Try it free. Keep it only if it works for you.</p>
          <div className="bg-card border-2 border-primary rounded-2xl overflow-hidden shadow-elevated relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-safe text-white text-xs font-bold px-4 py-1 rounded-full shadow">Most Popular</span>
            </div>
            <div className="bg-primary px-6 py-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <BadgeCheck className="h-5 w-5 text-white" />
                <span className="font-bold text-white text-xl">Verified Badge</span>
              </div>
              <span className="text-xs" style={{ background: "rgba(255,255,255,0.2)", color: "white", padding: "2px 12px", borderRadius: "20px" }}>Per domain · Cancel anytime</span>
            </div>
            <div className="p-6 space-y-5">
              <div className="text-center space-y-1">
                <p className="text-5xl font-bold text-safe">FREE</p>
                <p className="text-sm text-muted-foreground">for your first month</p>
                <p className="text-sm font-medium text-foreground">Then just $10 / month</p>
              </div>
              <div className="space-y-3 pt-3 border-t">
                {[
                  "No credit card required to apply",
                  "No charge if your site doesn't pass review",
                  "Cancel before month 2, you pay nothing",
                  "Everything included — no add-ons or upgrades",
                  "Badge deactivates if you cancel (no lingering false trust)",
                ].map((f) => (
                  <div key={f} className="flex items-start gap-3">
                    <CheckCircle className="h-4 w-4 text-safe shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground text-left">{f}</span>
                  </div>
                ))}
              </div>
              <Button
                className="w-full h-auto py-3.5 text-base font-semibold"
                size="lg"
                onClick={() => document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" })}
              >
                Get Verified — First Month Free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <p className="text-xs text-muted-foreground">
                We only charge after your site passes the eligibility check and your badge is live.
                If your site doesn't pass, you pay nothing — ever.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── APPLICATION FORM (UNTOUCHED) ───────────────────────────────────── */}
      <section className="py-16 bg-gradient-to-b from-accent/20 to-background" id="apply-form">
        <div className="container max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">Apply for Your Verified Badge</h2>
            <p className="text-muted-foreground mt-2">First month free. We'll review your site and get back to you within 1–2 business days.</p>
          </div>

          <div className="bg-card border rounded-2xl p-6 md:p-8 space-y-6 shadow-elevated">
            {/* Your Details */}
            <div className="space-y-4">
              <p className="font-semibold text-foreground border-b pb-2">Your Details</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input id="fullName" placeholder="John Smith" value={form.fullName} onChange={(e) => handleChange("fullName", e.target.value)} className={errors.fullName ? "border-destructive" : ""} />
                  {errors.fullName && <p className="text-xs text-destructive">{errors.fullName}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input id="email" type="email" placeholder="you@example.com" value={form.email} onChange={(e) => handleChange("email", e.target.value)} className={errors.email ? "border-destructive" : ""} />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="country">Country *</Label>
                <Input id="country" placeholder="United States" value={form.country} onChange={(e) => handleChange("country", e.target.value)} className={errors.country ? "border-destructive" : ""} />
                {errors.country && <p className="text-xs text-destructive">{errors.country}</p>}
              </div>
            </div>

            {/* Your Website */}
            <div className="space-y-4">
              <p className="font-semibold text-foreground border-b pb-2">Your Website</p>
              <div className="space-y-1.5">
                <Label htmlFor="domain">Website Domain *</Label>
                <Input id="domain" placeholder="yourwebsite.com" value={form.domain} onChange={(e) => handleChange("domain", e.target.value)} className={errors.domain ? "border-destructive" : ""} />
                {errors.domain && <p className="text-xs text-destructive">{errors.domain}</p>}
                <p className="text-xs text-muted-foreground">Enter without https:// e.g. mystore.com</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="category">Website Category *</Label>
                <select id="category" value={form.category} onChange={(e) => handleChange("category", e.target.value)} className={`w-full h-10 rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.category ? "border-destructive" : "border-input"}`}>
                  <option value="">Select category...</option>
                  {WEBSITE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {errors.category && <p className="text-xs text-destructive">{errors.category}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="description">Describe Your Website *</Label>
                <textarea id="description" rows={3} placeholder="Tell us what your website offers, what you sell or provide, and who your customers are..." value={form.description} onChange={(e) => handleChange("description", e.target.value)} className={`w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none placeholder:text-muted-foreground ${errors.description ? "border-destructive" : "border-input"}`} />
                {errors.description && <p className="text-xs text-destructive">{errors.description}</p>}
                <p className="text-xs text-muted-foreground italic">This description will be publicly displayed on your verification page to help visitors understand your business.</p>
              </div>
            </div>

            {/* Verification Info */}
            <div className="space-y-4">
              <p className="font-semibold text-foreground border-b pb-2">Additional Verification Info</p>
              <div className="space-y-1.5">
                <Label htmlFor="websiteAge">How long has your website been active? *</Label>
                <select id="websiteAge" value={form.websiteAge} onChange={(e) => handleChange("websiteAge", e.target.value)} className={`w-full h-10 rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.websiteAge ? "border-destructive" : "border-input"}`}>
                  <option value="">Select...</option>
                  {WEBSITE_AGE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                {errors.websiteAge && <p className="text-xs text-destructive">{errors.websiteAge}</p>}
              </div>
              {[
                { field: "hasPrivacy", label: "Do you have a privacy policy on your website? *" },
                { field: "hasTerms", label: "Do you have a terms of service on your website? *" },
                { field: "hasRefund", label: "Do you have a clear refund or cancellation policy? *" },
                { field: "isLive", label: "Is your website currently live and accessible? *" },
              ].map((item) => (
                <div key={item.field} className="space-y-1.5">
                  <Label>{item.label}</Label>
                  <div className="flex gap-4">
                    {["Yes", "No"].map((opt) => (
                      <label key={opt} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name={item.field} value={opt} checked={form[item.field as keyof typeof form] === opt} onChange={(e) => handleChange(item.field, e.target.value)} className="accent-primary" />
                        <span className="text-sm text-foreground">{opt}</span>
                      </label>
                    ))}
                  </div>
                  {errors[item.field] && <p className="text-xs text-destructive">{errors[item.field]}</p>}
                </div>
              ))}
              <div className="space-y-1.5">
                <Label htmlFor="additionalInfo">Any additional information you want us to know? <span className="text-muted-foreground">(Optional)</span></Label>
                <textarea id="additionalInfo" rows={3} placeholder="Anything else that would help us review your website..." value={form.additionalInfo} onChange={(e) => handleChange("additionalInfo", e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none placeholder:text-muted-foreground" />
              </div>
            </div>

            {/* Privacy note */}
            <div className="flex items-start gap-2.5 bg-accent/50 rounded-lg p-3">
              <Lock className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">We never sell your data.</span>{" "}
                Your information is used solely to verify your website and manage your badge subscription. See our{" "}
                <a href="/privacy" className="text-primary underline underline-offset-2">Privacy Policy</a>.
              </p>
            </div>

            {submitError && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                <p className="text-sm text-destructive">{submitError}</p>
              </div>
            )}

            <Button onClick={handleSubmit} className="w-full h-auto min-h-11 py-3 px-4 whitespace-normal text-center leading-snug" size="lg" disabled={loading}>
              {loading ? "Submitting..." : "Submit Application — First Month Free"}
              {!loading && <ArrowRight className="ml-2 h-4 w-4 shrink-0" />}
            </Button>
          </div>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────────────────── */}
      <section className="py-14 border-t bg-background">
        <div className="container max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">Common Questions</h2>
          <div className="space-y-3">
            {FAQS.map((item, i) => (
              <div key={i} className="border rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-accent/30 transition-colors">
                  <span className="font-medium text-foreground text-sm">{item.q}</span>
                  {openFaq === i ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-muted-foreground animate-fade-in border-t pt-3">{item.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ──────────────────────────────────────────────────────── */}
      <section className="py-16" style={{ background: "linear-gradient(135deg, #0f2744 0%, #1a3a5c 100%)" }}>
        <div className="container text-center space-y-5 max-w-xl mx-auto">
          <div className="flex justify-center">
            <BadgeSVG size={56} />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#e2e8f0" }}>
            Your visitors are checking. Make sure they like what they find.
          </h2>
          <p style={{ color: "#90cdf4" }}>First month free. Takes 2 minutes to apply. No credit card required.</p>
          <Button
            size="lg"
            className="h-auto py-3.5 px-8 text-base font-semibold"
            style={{ background: "linear-gradient(135deg, #38bdf8, #0ea5e9)", color: "#fff", border: "none" }}
            onClick={() => document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" })}
          >
            Apply Now — It's Free to Start
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>
    </Layout>
  );
}
